import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import authService from '../services/authService';
import { toast } from 'react-toastify';
import ProfileAvatar from '../components/common/ProfileAvatar';
import DeleteConfirmModal from '../components/common/DeleteConfirmModal';
import '../styles/AdminPage.css';

const PER_PAGE_OPTIONS = [5, 10, 20, 50];

const emptyForm = () => ({ username: '', password: '', van_no: '', place: '' });

export default function AdminPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Active nav button: filled dark-pink bg + white text
  // Inactive: white bg + pink text + pink border
  const navCls = (path) =>
    `adp__nav-btn${location.pathname === path ? ' adp__nav-btn--active' : ''}`;

  const [admins,   setAdmins]   = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchQ,  setSearchQ]  = useState('');
  const [page,     setPage]     = useState(1);
  const [perPage,  setPerPage]  = useState(10);

  // Add / Edit modal
  const [showModal,    setShowModal]    = useState(false);
  const [editTarget,   setEditTarget]   = useState(null);
  const [form,         setForm]         = useState(emptyForm());
  const [saving,       setSaving]       = useState(false);
  const [formErrors,   setFormErrors]   = useState({});
  const [showPassword, setShowPassword] = useState(false);

  // Delete confirm modal
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting,     setDeleting]     = useState(false);

  /* ── Load ── */
  const load = () =>
    authService.getUsers()
      .then(d => { setAdmins(d); setFiltered(d); })
      .catch(() => toast.error('Failed to load admins'));

  useEffect(() => { load(); }, []);

  /* ── Search filter — full word match only ── */
  useEffect(() => {
    if (!searchQ.trim()) { setFiltered(admins); setPage(1); return; }
    const q = searchQ.trim().toLowerCase();
    const wordRegex = new RegExp(`\\b${q}\\b`, 'i');
    setFiltered(admins.filter(u =>
      wordRegex.test(u.username  || '') ||
      wordRegex.test(u.van_no   || '') ||
      wordRegex.test(u.place    || '') ||
      wordRegex.test(`${u.first_name || ''} ${u.last_name || ''}`)
    ));
    setPage(1);
  }, [searchQ, admins]);

  /* ── Pagination ── */
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const slice = filtered.slice((page - 1) * perPage, page * perPage);

  /* ── Page numbers ── */
  const buildPages = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const s = new Set([1, 2, 3, 4, 5, totalPages]);
    if (page > 5 && page < totalPages) { s.add(page - 1); s.add(page); s.add(page + 1); }
    return Array.from(s).sort((a, b) => a - b);
  };

  /* ── XLS export ── */
  const exportXLS = () => {
    const rows = [
      ['Van No', 'Username', 'Created Date', 'Total Scans', 'Place'],
      ...filtered.map(u => [
        u.van_no || 'N/A',
        u.username,
        u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A',
        u.total_scans ?? 0,
        u.place || 'N/A',
      ]),
    ];
    const blob = new Blob([rows.map(r => r.join(',')).join('\n')], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    Object.assign(document.createElement('a'), { href: url, download: 'admins.csv' }).click();
    URL.revokeObjectURL(url);
  };

  /* ── Open add modal ── */
  const openAdd = () => {
    setEditTarget(null);
    setForm(emptyForm());
    setShowPassword(false);
    setShowModal(true);
  };

  /* ── Open edit modal ── */
  const openEdit = (admin) => {
    setEditTarget(admin);
    setForm({
      username: admin.username || '',
      password: '',
      van_no:   admin.van_no  || '',
      place:    admin.place   || '',
    });
    setShowPassword(false);
    setShowModal(true);
  };

  /* ── Close modal ── */
  const closeModal = () => {
    setShowModal(false);
    setEditTarget(null);
    setForm(emptyForm());
    setFormErrors({});
    setShowPassword(false);
  };

  /* ── Save (create or update) ── */
  const handleSave = async (e) => {
    e.preventDefault();

    // Inline validation
    const errs = {};
    if (!form.van_no.trim()) errs.van_no = 'Van No is required';
    if (!form.place.trim()) errs.place = 'Place is required';
    if (!form.username.trim()) errs.username = 'Username is required';
    if (!editTarget) {
      if (!form.password.trim()) errs.password = 'Password is required';
      else if (form.password.trim().length < 8) errs.password = 'Password must be at least 8 characters';
    } else {
      if (form.password.trim() && form.password.trim().length < 8)
        errs.password = 'Password must be at least 8 characters';
    }
    if (Object.keys(errs).length > 0) { setFormErrors(errs); return; }
    setFormErrors({});

    setSaving(true);
    try {
      if (editTarget) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await authService.updateUser(editTarget.id, payload);
        toast.success('Admin updated');
      } else {
        await authService.register({ ...form, role: 'admin' });
        toast.success('Admin created');
      }
      closeModal();
      load();
    } catch (err) {
      const data = err.response?.data;
      if (data && typeof data === 'object') {
        const errsFromApi = {};
        Object.keys(data).forEach(k => {
          errsFromApi[k] = Array.isArray(data[k]) ? data[k][0] : data[k];
        });
        setFormErrors(errsFromApi);
      } else {
        toast.error('Save failed');
      }
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ── */
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await authService.deleteUser(deleteTarget.id);
      toast.success('Admin deleted');
      setDeleteTarget(null);
      load();
    } catch {
      toast.error('Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (admin) => {
    try {
      await authService.updateUser(admin.id, { is_active: !admin.is_active });
      toast.success(admin.is_active ? 'Admin disabled' : 'Admin activated');
      load();
    } catch {
      toast.error('Unable to update admin status');
    }
  };

  const pageNums = buildPages();

  return (
    <div className="adp__root">

      {/* ══ 1. NAVBAR ══ */}
      <nav className="adp__navbar">
        <span className="adp__nav-title">Super Admin</span>
        <div className="adp__nav-actions">
          {/* View Admins — active on /admin */}
          <button className={navCls('/admin')} onClick={() => navigate('/admin')}>
            View Admins
          </button>
          {/* Start Analysis — active on /patients */}
          <button className={navCls('/patients')} onClick={() => navigate('/patients')}>
            Start Analysis
          </button>
          {/* Dashboard — active on /cases */}
          <button className={navCls('/cases')} onClick={() => navigate('/cases')}>
            Dashboard
          </button>
          <ProfileAvatar />
        </div>
      </nav>

      {/* ══ 2. SUB-BAR ══ */}
      <div className="adp__subbar">
        <span className="adp__subbar-title">Admin Details</span>
        <div className="adp__subbar-right">
          <div className="adp__search-wrap">
            <input
              type="text"
              className="adp__search-input"
              placeholder="Search"
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
            />
            <i className="fa-solid fa-magnifying-glass adp__search-icon" />
          </div>
          <button className="adp__btn-xls" onClick={exportXLS} title="Export to Excel">
            <span className="adp__xls-icon">
              <i className="fa-solid fa-file-excel" />
            </span>
          </button>
          <button className="adp__btn-add-van" onClick={openAdd}>
            Add Admin
          </button>
        </div>
      </div>

      {/* ══ 3. PINK BODY ══ */}
      <div className="adp__pink-body">
        <div className="adp__card">

          {/* Pink thead */}
          <div className="adp__thead">
            <div className="adp__col adp__col--van" style={{ whiteSpace: 'nowrap' }}>Van No</div>
            <div className="adp__col adp__col--user" style={{ whiteSpace: 'nowrap' }}>User name</div>
            <div className="adp__col adp__col--date" style={{ whiteSpace: 'nowrap' }}>Created Date</div>
            <div className="adp__col adp__col--scan" style={{ whiteSpace: 'nowrap' }}>Total no scan</div>
            <div className="adp__col adp__col--place" style={{ whiteSpace: 'nowrap' }}>Place</div>
            <div className="adp__col adp__col--status" style={{ whiteSpace: 'nowrap' }}>Active / Disable</div>
            <div className="adp__col adp__col--action" style={{ whiteSpace: 'nowrap' }}>View</div>
          </div>

          {/* Rows */}
          <div className="adp__rows-area">
            {slice.length === 0 ? (
              <div className="adp__empty">
                <i className="fa-solid fa-users-slash fa-2x mb-2 d-block" />
                No admins found
              </div>
            ) : (
              slice.map((u) => (
                <div key={u.id} className="adp__row">
                  <div className="adp__col adp__col--van adp__cell-bold">
                    {u.van_no || 'N/A'}
                  </div>
                  <div className="adp__col adp__col--user">{u.username}</div>
                  <div className="adp__col adp__col--date">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString('en-US', { year:'numeric', month:'short', day:'numeric' }) : 'N/A'}
                  </div>
                  <div className="adp__col adp__col--scan adp__cell-muted">
                    {u.total_scans ?? '0'}
                  </div>
                  <div className="adp__col adp__col--place adp__cell-muted">
                    {u.place || 'N/A'}
                  </div>
                  <div className="adp__col adp__col--status" style={{ display: 'flex', alignItems: 'center'}}>
                    <button
                      aria-pressed={!u.is_active}
                      onClick={() => handleToggleActive(u)}
                      title={u.is_active ? 'Disable this admin' : 'Activate this admin'}
                      style={{
                        width: 76,
                        height: 36,
                        borderRadius: 999,
                        padding: 0,
                        border: '2px solid #4ecdc4',
                        background: u.is_active ? '#4ecdc4' : '#f8e8ec',
                        display: 'inline-block',
                        position: 'relative',
                        cursor: 'pointer',
                      }}
                    >
                      <span style={{
                        display: 'block',
                        width: 30,
                        height: 30,
                        borderRadius: '50%',
                        background: u.is_active ? '#e6fdfb' : '#e9f1f4',
                        position: 'absolute',
                        top: '50%',
                        transform: `translateY(-50%) translateX(${u.is_active ? '36px' : '8px'})`,
                        transition: 'transform 180ms ease',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.08)'
                      }} />
                    </button>
                  </div>
                  <div className="adp__col adp__col--action adp__cell-actions">
                    <button
                      className="adp__btn-edit"
                      title="Edit"
                      onClick={() => openEdit(u)}
                    >
                      <i className="fa-solid fa-pen" />
                    </button>
                    <button
                      className="adp__btn-delete"
                      title="Delete"
                      onClick={() => setDeleteTarget(u)}
                    >
                      <i className="fa-solid fa-trash" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* ══ PAGINATOR — BOTTOM ══ */}
          <div className="adp__pager-row">
            <div className="adp__pager-left">
              <span className="adp__pager-label">Show</span>
              <select
                className="adp__per-page-select"
                value={perPage}
                onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
              >
                {PER_PAGE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <span className="adp__pager-label">Row</span>
            </div>

            <div className="adp__pager-btns">
              <button
                className="adp__pager-btn adp__pager-arrow"
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
              >
                <i className="fa-solid fa-chevron-left" />
              </button>

              {pageNums.map((p, i, arr) => (
                <React.Fragment key={p}>
                  {i > 0 && arr[i - 1] !== p - 1 && (
                    <button className="adp__pager-btn adp__pager-ellipsis" disabled>…</button>
                  )}
                  <button
                    className={`adp__pager-btn${p === page ? ' adp__pager-active' : ''}`}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                </React.Fragment>
              ))}

              <button
                className="adp__pager-btn adp__pager-arrow"
                disabled={page === totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                <i className="fa-solid fa-chevron-right" />
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* ══ ADD / EDIT MODAL ══ */}
      {showModal && (
        <div
          key={editTarget ? `edit-${editTarget.id}` : 'add'}
          className="dcm__overlay"
          onClick={closeModal}
        >
          <div className="dcm__modal-container adp__modal-lg" onClick={e => e.stopPropagation()}>

            {/* Background image layer */}
            <div className="dcm__bg" />
            {/* Semi-transparent tint over the bg image */}
            <div className="dcm__bg-tint" />

            <div className="dcm__content">
              <div className="d-flex align-items-center justify-content-between mb-4">
                <h6 className="fw-bold mb-0 dcm__title">
                  {editTarget ? 'Edit Admin' : 'Add New Admin'}
                </h6>
                <button className="dcm__close-btn" onClick={closeModal}>X</button>
              </div>

              <form onSubmit={handleSave} noValidate>
                <div className="adp__form-grid mb-4">

                  {/* Van No */}
                  <div className="col-12 mb-3">
                    <label className="form-label fw-bold small">Van No *</label>
                    <input
                      className="form-control form-control-sm"
                      type="text"
                      placeholder="e.g. 01"
                      maxLength={50}
                      value={form.van_no}
                      onChange={e => { setForm(p => ({ ...p, van_no: e.target.value })); setFormErrors(p => ({ ...p, van_no: '' })); }}
                    />
                    {formErrors.van_no && <div className="invalid-feedback d-block">{formErrors.van_no}</div>}
                  </div>

                  {/* Place */}
                  <div className="col-12 mb-3">
                    <label className="form-label fw-bold small">Place *</label>
                    <input
                      className="form-control form-control-sm"
                      type="text"
                      placeholder="e.g. Chennai"
                      maxLength={50}
                      value={form.place}
                      onChange={e => { setForm(p => ({ ...p, place: e.target.value.replace(/[^a-zA-Z\s]/g, '') })); setFormErrors(p => ({ ...p, place: '' })); }}
                    />
                    {formErrors.place && <div className="invalid-feedback d-block">{formErrors.place}</div>}
                  </div>

                  {/* Username */}
                  <div className="col-12 mb-3">
                    <label className="form-label fw-bold small">Username *</label>
                    <input
                      className="form-control form-control-sm"
                      type="text"
                      required
                      autoComplete="off"
                      maxLength={50}
                      value={form.username}
                      onChange={e => { setForm(p => ({ ...p, username: e.target.value.replace(/[^a-zA-Z]/g, '') })); setFormErrors(p => ({ ...p, username: '' })); }}
                    />
                    {formErrors.username && <div className="invalid-feedback d-block">{formErrors.username}</div>}
                  </div>

                  {/* Password */}
                  <div className="col-12 mb-3">
                    <label className="form-label fw-bold small">
                      Password {editTarget ? '(leave blank to keep)' : '*'}
                    </label>
                    {/* Container for Input and Eye Icon */}
                    <div style={{ position: 'relative' }}>
                      <input
                        className="form-control form-control-sm"
                        type={showPassword ? 'text' : 'password'}
                        required={!editTarget}
                        minLength={8}
                        maxLength={50}
                        autoComplete="new-password"
                        value={form.password}
                        onChange={e => { setForm(p => ({ ...p, password: e.target.value })); setFormErrors(p => ({ ...p, password: '' })); }}
                        style={{ paddingRight: '40px' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(prev => !prev)}
                        style={{
                          position: 'absolute',
                          right: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#888',
                          padding: '0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '24px',
                          height: '24px',
                        }}
                        title={showPassword ? 'Hide password' : 'Show password'}
                      >
                        <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </button>
                    </div>
                    {/* Error message moved outside the relative container to fix alignment */}
                    {formErrors.password && <div className="invalid-feedback d-block">{formErrors.password}</div>}
                  </div>

                </div>

                <div className="d-flex justify-content-center gap-3">
                  <button type="button" className="btn dcm__btn-cancel px-4" onClick={closeModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn dcm__btn-delete px-4" disabled={saving}>
                    {saving ? 'Saving...' : (editTarget ? 'Update' : 'Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ══ DELETE CONFIRM MODAL ══ */}
      {deleteTarget && (
        <DeleteConfirmModal
          patientName={deleteTarget.username}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
}